import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Gift, ArrowRight, Check } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCES = [
  { id: "brand-checklist", name: "Brand Checklist", desc: "The 20-point gut check every small brand needs." },
  { id: "social-sizing", name: "Social Sizing Guide", desc: "Every platform, every dimension, one cheat sheet." },
  { id: "brand-questionnaire", name: "Brand Questionnaire", desc: "The exact questions STAX asks before designing." },
  { id: "launch-checklist", name: "Website Launch Checklist", desc: "Don't go live without ticking these boxes." },
];

export const Freebies = ({ ch = "06" }) => {
  const [selected, setSelected] = useState(RESOURCES[0].id);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [unlocked, setUnlocked] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Drop your email so we can send it over.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/freebie`, { email, resource: selected });
      if (r.data.download_available) {
        setUnlocked(selected);
        toast.success("Unlocked — hit download!");
      } else {
        setUnlocked("soon");
        toast.success("You're on the list — it lands in your inbox soon.");
      }
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="freebies" data-testid="freebies-section" className="relative overflow-hidden bg-staxcyan py-24 sm:py-32 border-y-4 border-ink">
      <div className="ink-dot absolute -left-24 bottom-0 h-72 w-72 bg-staxyellow" />
      <div className="ink-dot absolute right-10 -top-16 h-48 w-48 bg-staxmagenta" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal><SectionTag color="bg-white text-ink" ch={ch}>Creative Resources</SectionTag></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-display uppercase leading-[0.92] text-4xl sm:text-5xl lg:text-6xl text-white">
                Free stuff.<br />Because <span className="text-staxmagenta">why not?</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-5 max-w-sm font-medium text-ink/70 text-base">
                Steal my process. Downloadable guides and checklists — free, useful, and dangerously colorful.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              {unlocked === selected ? (
                <a
                  data-testid="freebie-download-button"
                  href={`${API}/freebies/${selected}/download`}
                  className="group mt-8 inline-flex items-center gap-2 border-2 border-ink bg-ink px-6 py-4 font-display uppercase text-sm text-white shadow-hard-sm transition-transform hover:-translate-y-0.5"
                >
                  <Download size={17} /> Download {RESOURCES.find((r) => r.id === selected)?.name}
                </a>
              ) : unlocked === "soon" ? (
                <p data-testid="freebie-soon-message" className="mt-8 inline-flex items-center gap-2 border-2 border-ink bg-white px-5 py-3.5 font-mono2 text-xs font-bold uppercase tracking-widest text-ink">
                  <Check size={15} className="text-staxmagenta" /> On its way to your inbox
                </p>
              ) : (
                <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
                  <input
                    data-testid="freebie-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 border-2 border-ink bg-white px-4 py-3.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-staxmagenta placeholder:text-ink/40"
                  />
                  <button
                    data-testid="freebie-submit-button"
                    type="submit"
                    disabled={sending}
                    className="group inline-flex items-center justify-center gap-2 border-2 border-ink bg-ink px-6 py-3.5 font-display uppercase text-sm text-white shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {sending ? "Unlocking..." : "Unlock It"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </form>
              )}
              {(unlocked === selected || unlocked === "soon") && (
                <button
                  data-testid="freebie-another-button"
                  onClick={() => setUnlocked(null)}
                  className="mt-4 block font-mono2 text-[11px] font-bold uppercase tracking-[0.25em] text-ink/60 underline hover:text-ink"
                >
                  Get another resource
                </button>
              )}
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {RESOURCES.map((r, i) => (
              <Reveal key={r.id} delay={0.08 * i}>
                <button
                  data-testid={`freebie-${r.id}`}
                  onClick={() => { setSelected(r.id); setUnlocked(null); }}
                  className={`group relative w-full border-4 border-ink p-6 text-left transition-all hover:-translate-y-1 ${
                    selected === r.id ? "bg-ink text-white shadow-hard-yellow" : "bg-white text-ink shadow-hard-sm"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Gift size={22} className={selected === r.id ? "text-staxyellow" : "text-ink"} />
                    <Download size={18} className="opacity-40 transition-opacity group-hover:opacity-100" />
                  </div>
                  <h3 className="mt-4 font-display uppercase text-xl">{r.name}</h3>
                  <p className={`mt-2 text-sm font-medium ${selected === r.id ? "text-white/70" : "text-ink/60"}`}>{r.desc}</p>
                  {selected === r.id && (
                    <span className="mt-3 inline-block font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-staxyellow">
                      Selected — drop your email
                    </span>
                  )}
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
